import type { CofQuizQuestion, CofQuizVerdict } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './cof-quiz-feedback.module.css'

export interface CofQuizFeedbackProps {
  question: CofQuizQuestion
  verdict: CofQuizVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Human-readable accepted-answer string per question kind, surfaced on the
 * incorrect branch so the user learns the right answer. Relationship + spelling
 * kinds list the accepted key set; the signature kind describes the count.
 */
const describeAnswer = (question: CofQuizQuestion): string => {
  switch (question.kind) {
    case 'find-relative':
    case 'find-closely-related':
    case 'find-parallel':
      return [...new Set(question.acceptedAnswers)].join(', ')
    case 'spell-enharmonic':
      return [...new Set(question.acceptedSpellings)].join(', ')
    case 'count-signature': {
      if (question.acceptedCount === 0) {
        return 'no sharps or flats'
      }
      const plural = question.acceptedCount > 1 ? 's' : ''
      return `${question.acceptedCount} ${question.promptDirection}${plural}`
    }
  }
}

/**
 * CoF Wheel Quiz feedback surface — verdict + replay + next. The incorrect
 * branch surfaces the accepted answer (key set, spelling set, or signature
 * count, depending on the question kind) so the user learns the relationship.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §6.4
 */
export const CofQuizFeedback: FC<CofQuizFeedbackProps> = props => {
  const { question, verdict, onHearAgain, onNext } = props
  const dedupedAnswers = describeAnswer(question)
  return (
    <section className={styles.panel} aria-label="Quiz feedback">
      {verdict.correct ? (
        <p className={styles.correct}>Correct!</p>
      ) : (
        <p className={styles.incorrect}>Not quite. Accepted answers: {dedupedAnswers}.</p>
      )}
      <div className={styles.actions}>
        <Button variant="outlined" size="small" onClick={onHearAgain}>
          Hear again
        </Button>
        <Button variant="contained" size="small" onClick={onNext}>
          Next
        </Button>
      </div>
    </section>
  )
}
